// import React from 'react';
// import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { HashLink } from 'react-router-hash-link';
import { styled } from '@mui/material/styles';
import { AffinityDto } from 'models/dtoTypes';
import { smartGovernancePrimaryColor } from 'kosmosConfig';
import { useTranslation } from 'react-i18next';

const PREFIX = '_sdgScoreClob';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  anchorTitle: `${PREFIX}-anchorTitle`,
  dimension: `${PREFIX}-dimension`,
  affinityContainer: `${PREFIX}-affinityContainer`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'stretch',

  [`& .${classes.affinityContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: 10,
    '& .dot': {
      verticalAlign: 'middle',
    },
    '& .circle': {
      verticalAlign: 'middle',
      border: '1px solid #707070',
    },
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: 7,
    '& a': {
      textDecorationColor: '#084fa3',
    },
    '& a:active': {
      textDecorationColor: '#3544d0',
    },
  },

  [`& .${classes.title}`]: {
    flex: 1,
    color: '#1c1c1c',
  },
  [`& .${classes.anchorTitle}`]: {
    flex: 1,
    color: '#084fa3',
  },
  [`& .${classes.dimension}`]: {
    alignSelf: 'center',
    width: 100,
    fontSize: 12,
    textTransform: 'capitalize',
    color: 'rgba(0,0,0,0.5)',
  },
}));

export interface ISdgData {
  sdgId?: string;
  anchor?: string;
  itemNumber?: number;
  title?: string;
  weight?: 'bold' | 'regular';
  iccDimension?: string;
  affinity?: AffinityDto;
  secondaryAffinity?: AffinityDto;
  affinityColor?: string; // specific colour
  withBorder?: boolean;
  withBg?: boolean;
  size?: 'default' | 'small';
}

const generateColor = (category: string): string => {
  switch (category) {
    case 'negative':
      return '#ff7c8f';
    case 'positive':
      return '#23c8a7';
    case 'neutral':
      return '#ffffff';
    case 'warning':
      return '#e6b12e';
    default:
      return '#666666';
  }
};

export default function SdgScoreClob(props: ISdgData): JSX.Element {
  const {
    sdgId,
    itemNumber,
    title,
    size = 'default',
    anchor,
    iccDimension,
    affinity,
    secondaryAffinity,
    affinityColor,
    withBorder,
    weight = 'bold',
    withBg,
  } = props;

  const { t } = useTranslation('default');

  const dimensionMapper = {
    'human-social': t('human-social'),
    environmental: t('environmental'),
    economic: t('economic'),
    'socio-economic': t('socioeconomic'),
  };

  const indicatorStyles =
    size === 'default'
      ? {
          width: 30,
          height: 30,
          borderRadius: 15,
        }
      : {
          width: 20,
          height: 20,
          borderRadius: 10,
        };
  const titleStyles =
    size === 'default'
      ? {
          fontSize: 16,
          fontWeight: weight,
        }
      : {
          fontSize: 12,
          fontWeight: weight,
        };

  const renderAffinity = (aff?: AffinityDto) => {
    const color = affinityColor ? affinityColor : generateColor(aff || '');
    return (
      <div
        className={classes.affinityContainer}
        style={{
          backgroundColor: withBg ? '#121212' : 'transparent',
          padding: size === 'default' ? 10 : 5,
        }}
      >
        <div
          className={aff === 'neutral' ? 'circle' : 'dot'}
          style={{ ...indicatorStyles, backgroundColor: color }}
        />
      </div>
    );
  };

  const renderSecondaryAffinity = (aff?: AffinityDto) => {
    const color = affinityColor ? affinityColor : generateColor(aff || '');
    return (
      <div
        className={classes.affinityContainer}
        style={{
          backgroundColor: withBg ? smartGovernancePrimaryColor : 'transparent',
          padding: size === 'default' ? 10 : 5,
          marginRight: 10,
        }}
      >
        <div
          className={aff === 'neutral' ? 'circle' : 'dot'}
          style={{ ...indicatorStyles, backgroundColor: color }}
        />
      </div>
    );
  };

  const scrollWithOffset = (el: HTMLElement) => {
    const yCoordinate: number = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -80;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const renderTitle = () => {
    if (anchor) {
      return (
        <div>
          {/* @ts-ignore */}
          <HashLink smooth to={`#${anchor}`} scroll={scrollWithOffset}>
            <div className={classes.anchorTitle} style={titleStyles}>
              {title}
            </div>
          </HashLink>
        </div>
      );
    } else {
      return (
        <div className={classes.title} style={titleStyles}>
          {title}
        </div>
      );
    }
  };

  const renderContent = () => {
    return (
      <div className={classes.content}>
        <div style={{ display: 'flex' }}>
          {itemNumber !== undefined && (
            <div style={{ width: 30, marginLeft: 10 }}>{itemNumber}.</div>
          )}
          <div style={{ flex: 1 }}>{renderTitle()}</div>
        </div>
      </div>
    );
  };

  const renderRecord = () => {
    const hasAffinity = affinity || affinityColor;
    const hasSecondaryAffinity = secondaryAffinity;
    return (
      <Root id={sdgId}>
        {hasAffinity && renderAffinity(affinity)}
        {hasAffinity && hasSecondaryAffinity && <div style={{ width: 10 }}></div>}
        {hasSecondaryAffinity && renderSecondaryAffinity(secondaryAffinity)}
        {(title || iccDimension) && renderContent()}
        {iccDimension && (
          <div className={classes.dimension}>{dimensionMapper[iccDimension] || iccDimension}</div>
        )}
      </Root>
    );
  };

  return (
    <Box
      component="div"
      sx={{
        mt: withBorder ? 1 : 0,
        mb: withBorder ? 1 : 0,
        border: withBorder ? '2px solid #808080' : null,
      }}
    >
      {renderRecord()}
    </Box>
  );
}
